/**
 * assets/dm-privacy-choices.js — reveals and wires the footer "Your privacy
 * choices" control (`snippets/dm-privacy-choices.liquid`, PLAN.md:3562,
 * Phase 9 unit U4), and ships PLAN.md:3562/3911's Global Privacy Control
 * fallback.
 *
 * ── THE ONE RULE — RENDER NOTHING, NOT A DEAD CONTROL ───────────────────────
 * The button in the DOM starts `hidden` and STAYS hidden until this file
 * confirms `window.Shopify.customerPrivacy.setTrackingConsent` is a real
 * function. That object does not exist until Shopify's privacy script has
 * loaded (`Settings → Customer privacy` must be configured at all) and MAY
 * NEVER EXIST on an unconfigured store — the same fact `assets/dm-
 * analytics.js`'s header states for the identical API, reused here rather
 * than re-derived. `Shopify.loadFeatures` arrives asynchronously with
 * `content_for_header`, so a single same-tick check is not enough; this file
 * runs the SAME bounded-retry shape `dm-analytics.js` uses for that reason —
 * an independent instance, not a shared module, because the two files are
 * separate Phase 5 / Phase 9 units with no agreed module boundary between
 * them and `Shopify.loadFeatures` is documented as safe to call more than
 * once. If the API never resolves, the button stays `hidden` forever — that
 * IS the "renders nothing" contract, not a failure of it.
 *
 * ── THE CLICK — ONE STEP, NOT A PREFERENCE MODAL ────────────────────────────
 * `setTrackingConsent({ sale_of_data: false }, callback)`. CPRA's own
 * regulations cap a "Do Not Sell or Share" control at two steps; one click is
 * the floor, not a shortcut taken. `callback` only updates the `aria-live`
 * status span named by the button's `aria-describedby` — it never rewrites
 * the button's own label, so the control stays recognisable, and clickable
 * again, rather than mutating into a differently-labelled control after one
 * use (the call is idempotent; a second click sets the same value again).
 * Shopify's own `setTrackingConsent` dispatches `visitorConsentCollected` on
 * completion, which `dm-analytics.js` already listens for — no direct call
 * from this file into that one is needed for the consent change to reach it.
 *
 * ── GLOBAL PRIVACY CONTROL — SHIPPED UNCONDITIONALLY, V8 STILL OPEN ─────────
 * PLAN.md:3562/3911 (V8): whether Shopify's storefront ALREADY honours
 * `navigator.globalPrivacyControl` / a `Sec-GPC` request header as an
 * opt-out signal is an UNVERIFIED FACT. PLAN requires written confirmation
 * from Shopify's own documentation or support, filed in `docs/08-privacy.md`
 * with a date, before anyone may claim either answer — "counsel signs off on
 * the platform's answer, not on this plan's summary of it." That file does
 * not exist yet (this unit did not create it — no evidence to file; see this
 * unit's progress file). PLAN's own instruction is that the fallback ships
 * "either way," so `gpcFallback()` below runs UNCONDITIONALLY, independent of
 * whether the button is ever shown or clicked: when
 * `navigator.globalPrivacyControl === true`, it silently calls the SAME
 * `setTrackingConsent({ sale_of_data: false })`, no click required. If
 * Shopify already honours GPC natively, this is a harmless idempotent
 * duplicate of a state that is already true. If it does not, this is the
 * actual fix. THIS FILE DOES NOT RESOLVE V8 — it only ships the fallback
 * PLAN says to ship regardless of the answer.
 *
 * Classic script, ES2020, no dependencies, no third-party network call.
 */
(function () {
  'use strict';

  var TICK_MS = 300;
  var TICKS_MAX = 34; // ~10s — the same bound assets/dm-analytics.js uses
  // for the identical `Shopify.loadFeatures` race, restated rather than
  // imported (see header: no shared module boundary between the two units).

  function privacyApi() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    return p && typeof p.setTrackingConsent === 'function' ? p : null;
  }

  var loadRequested = false;
  function requestPrivacy() {
    if (loadRequested || privacyApi()) return;
    var S = window.Shopify;
    if (!S || typeof S.loadFeatures !== 'function') return; // not up yet; next tick
    loadRequested = true;
    try {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], tick);
    } catch (error) {
      loadRequested = false;
    }
  }

  /** `callback` is optional — the GPC fallback below calls this with none. */
  function optOut(callback) {
    var p = privacyApi();
    if (!p) return false;
    try {
      p.setTrackingConsent({ sale_of_data: false }, function () {
        if (typeof callback === 'function') callback();
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  var gpcSent = false;
  function gpcFallback() {
    if (gpcSent || navigator.globalPrivacyControl !== true) return;
    // Only stop retrying once the call actually fired — a privacy API that
    // is not up yet must not permanently disarm this on the next tick.
    if (optOut()) gpcSent = true;
  }

  var bound = false;
  function reveal() {
    var p = privacyApi();
    var button = document.querySelector('[data-dm-privacy-choices]');
    if (!p || !button) return;
    button.hidden = false;
    if (bound) return;
    bound = true;
    var status = document.getElementById(button.getAttribute('aria-describedby') || '');
    button.addEventListener('click', function () {
      optOut(function () {
        if (status) status.textContent = button.getAttribute('data-dm-privacy-confirmed') || '';
      });
    });
  }

  var timer = null;
  var ticks = 0;
  function tick() {
    ticks += 1;
    requestPrivacy();
    reveal();
    gpcFallback();
    if (timer && ticks >= TICKS_MAX) {
      clearInterval(timer);
      timer = null;
    }
  }

  function poll() {
    if (timer) return;
    tick();
    if (!privacyApi()) timer = setInterval(tick, TICK_MS);
  }

  document.addEventListener('visitorConsentCollected', function () {
    reveal();
    gpcFallback();
  });

  poll();
})();
